// src/services/contentReports.js
// Faz 1D Görev 4 — İçerik şikayet callable istemci sarmalayıcısı.
//
// Sunucu tarafı: `functions/src/content_reports.ts` — `submitContentReport`
// onCall (europe-west3, 15s, 128MiB). Bu modül UI'den çağrılacak tek giriş
// noktası; rate-limit/validation/dedup sunucuda çözülür.
//
// Dönüş sözleşmesi:
//   { ok: true, reportId }             → gönderildi / overwrite edildi
//   { ok: false, code, message }       → kullanıcıya gösterilebilir TR mesajı

import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import {
  REPORT_REASON_LIST,
  REPORT_TARGET_TYPE_LIST,
  REPORT_DETAIL_MAX_LENGTH,
} from "../constants/reportSchema";

// Firebase Functions v2 HttpsError code → TR mesaj.
// Tip `Record<string,string>`: anahtar RUNTIME'da gelen `err.code` (bilinmeyen
// bir kod da gelebilir) — literal anahtar tipi bu aramayı tarif etmiyor.
const CODE_MESSAGES: Readonly<Record<string, string>> = Object.freeze({
  unauthenticated: "Şikayet için oturum açmalısın.",
  "invalid-argument": "Eksik veya hatalı bilgi.",
  "failed-precondition": "Kendi içeriğini veya profilini şikayet edemezsin.",
  "resource-exhausted": "Çok fazla şikayet gönderdin. Lütfen daha sonra tekrar dene.",
  internal: "Şikayet kaydedilemedi. Lütfen tekrar dene.",
});

/** `mapError`'ın bir hata nesnesinden OKUDUĞU yüzey (FunctionsError alt kümesi). */
type CallableErrorLike = { code?: string; message?: string };

/** Başarısız şikayet sonucu — `ok:false` dalı. */
export type ContentReportFailure = { ok: false; code: string; message: string };

/** Başarılı şikayet sonucu — `ok:true` dalı. */
export type ContentReportSuccess = { ok: true; reportId: string };

/** `submitContentReport` dönüş sözleşmesi (dosya başındaki blokla aynı). */
export type ContentReportResult = ContentReportSuccess | ContentReportFailure;

function mapError(err: unknown): ContentReportFailure {
  // `strict` altında `catch` değişkeni `unknown` gelir; gövde eskiden beri
  // yalnız `code`/`message` okuyor — daraltma o iki alanla sınırlı.
  const e = err as CallableErrorLike | null | undefined;
  const rawCode = String(e?.code || "").replace(/^functions\//, "");
  const message =
    CODE_MESSAGES[rawCode] ||
    e?.message ||
    "Şikayet gönderilemedi.";
  return { ok: false, code: rawCode || "unknown", message };
}

/**
 * `submitContentReport` girdi sözleşmesi.
 *
 * ⚠️ Alanlar gövdeden türetildi, eski JSDoc'tan DEĞİL: eski JSDoc
 * `targetType`i `"clip"|"comment"|"reply"` diye daraltıyordu, oysa gövde
 * değeri `String(...).toLowerCase()` yapıp `REPORT_TARGET_TYPE_LIST` ile
 * RUNTIME'da doğruluyor ve çağıranlar (`ReportUserModal`, `StoryModal*`)
 * `"user"` gönderiyor. Aynı şekilde her alan `String(x || "")` süzgecinden
 * geçtiği için `null`/`undefined` de meşru girdidir.
 */
export type ContentReportInput = {
  /** rapor hedefinin tipi — `REPORT_TARGET_TYPE_LIST` ile runtime doğrulanır */
  targetType?: string | null;
  /** rapor hedefinin id'si */
  targetId?: string | null;
  /** comment/reply için parent clip */
  clipId?: string | null;
  /** reply için parent comment */
  commentId?: string | null;
  /** reply için reply id'si */
  replyId?: string | null;
  /** REPORT_REASON whitelist — runtime doğrulanır */
  reason?: string | null;
  /** serbest metin (≤300 char) */
  detail?: string | null;
};

/**
 * `submitContentReport` callable'ı çağırır.
 */
export async function submitContentReport(
  input: ContentReportInput,
): Promise<ContentReportResult> {
  const targetType = String(input?.targetType || "").toLowerCase();
  const targetId = String(input?.targetId || "").trim();
  const reason = String(input?.reason || "").toLowerCase();
  const detail = String(input?.detail || "").trim().slice(0, REPORT_DETAIL_MAX_LENGTH);

  if (!REPORT_TARGET_TYPE_LIST.includes(targetType as any)) {
    return { ok: false, code: "invalid-argument", message: "Geçersiz hedef tipi." };
  }
  if (!targetId) {
    return { ok: false, code: "invalid-argument", message: "Hedef bilgisi eksik." };
  }
  if (!REPORT_REASON_LIST.includes(reason as any)) {
    return { ok: false, code: "invalid-argument", message: "Sebep seçmelisin." };
  }

  try {
    const call = httpsCallable(functions, "submitContentReport");
    const res = await call({
      targetType,
      targetId,
      reason,
      ...(detail ? { detail } : {}),
      ...(input?.clipId ? { clipId: String(input.clipId).trim() } : {}),
      ...(input?.commentId ? { commentId: String(input.commentId).trim() } : {}),
      ...(input?.replyId ? { replyId: String(input.replyId).trim() } : {}),
    });
    const data = (res?.data || {}) as any;
    if (data?.success) {
      return { ok: true, reportId: String(data.reportId || "") };
    }
    return { ok: false, code: "unknown", message: "Şikayet gönderilemedi." };
  } catch (err) {
    return mapError(err);
  }
}
